import { useEffect, useState } from 'react';
import { Logo, ThemeToggle } from '../components';
import { formatDate } from '../api';

type ComponentStatus = 'operational' | 'degraded' | 'down' | 'maintenance';
interface Status {
  generatedAt: string;
  overall: ComponentStatus;
  components: { key: string; name: string; status: ComponentStatus; note: string | null }[];
  history: Record<string, ('operational' | 'degraded' | 'incident' | 'maintenance')[]>;
  historyDays: number;
  incidents: {
    active: Incident[];
    recent: Incident[];
  };
}
interface Incident {
  id: string;
  title: string;
  severity: 'minor' | 'major' | 'maintenance';
  status: 'investigating' | 'identified' | 'monitoring' | 'resolved';
  components: string[];
  startedAt: string;
  resolvedAt: string | null;
  updates: { id: string; status: string; body: string; createdAt: string }[];
}

const LABEL: Record<ComponentStatus, string> = {
  operational: 'Operational',
  degraded: 'Degraded',
  down: 'Outage',
  maintenance: 'Maintenance',
};
const HEADLINE: Record<ComponentStatus, string> = {
  operational: 'All systems operational',
  degraded: 'Some systems are degraded',
  down: 'We are experiencing an outage',
  maintenance: 'Planned maintenance in progress',
};

/** Public status page: no session, no dashboard chrome. Served at /status and on status.tallypg.com. */
export function StatusPage() {
  const [data, setData] = useState<Status | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const load = () =>
      fetch('/api/public/status')
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`status ${r.status}`))))
        .then((d) => setData(d as Status))
        .catch((e: Error) => setError(e.message));
    void load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="status-page">
      <ThemeToggle />
      <header className="status-head">
        <a href="https://tallypg.com" className="brand">
          <Logo />
          <span>TallyPG status</span>
        </a>
        <a href="https://app.tallypg.com" className="muted">
          Dashboard
        </a>
      </header>
      {error && <p className="error">Could not load status: {error}</p>}
      {data && (
        <>
          <div className={`status-banner ${data.overall}`}>
            <strong>{HEADLINE[data.overall]}</strong>
            <span className="muted">updated {formatDate(data.generatedAt)}</span>
          </div>
          {data.incidents.active.map((inc) => (
            <IncidentCard key={inc.id} inc={inc} />
          ))}
          <div className="card">
            {data.components.map((c) => (
              <div key={c.key} className="status-row">
                <div className="row between">
                  <div>
                    <strong>{c.name}</strong>
                    {c.note && <div className="muted">{c.note}</div>}
                  </div>
                  <span className={`status-pill ${c.status}`}>{LABEL[c.status]}</span>
                </div>
                <div className="uptime" title={`last ${data.historyDays} days`}>
                  {data.history[c.key]?.map((d, i) => (
                    <span key={i} className={`day ${d}`} />
                  ))}
                </div>
              </div>
            ))}
            <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
              {data.historyDays} days · <span className="day-key operational" /> operational{' '}
              <span className="day-key degraded" /> degraded <span className="day-key incident" /> incident{' '}
              <span className="day-key maintenance" /> maintenance
            </div>
          </div>
          <h2 style={{ marginTop: 28 }}>Past incidents</h2>
          {data.incidents.recent.length === 0 && <p className="muted">No incidents in the last 30 days.</p>}
          {data.incidents.recent.map((inc) => (
            <IncidentCard key={inc.id} inc={inc} />
          ))}
          <p className="muted" style={{ marginTop: 28, fontSize: 12 }}>
            Status is computed from live health checks every minute. Questions:{' '}
            <a href="mailto:support@tallypg.com">support@tallypg.com</a>
          </p>
        </>
      )}
    </div>
  );
}

function IncidentCard({ inc }: { inc: Incident }) {
  return (
    <div className={`card incident ${inc.severity}`}>
      <div className="row between">
        <strong>{inc.title}</strong>
        <span
          className={`badge ${inc.status === 'resolved' ? 'active' : inc.severity === 'major' ? 'failed' : 'provisioning'}`}
        >
          {inc.status}
        </span>
      </div>
      <div className="muted" style={{ fontSize: 12 }}>
        {inc.severity} · started {formatDate(inc.startedAt)}
        {inc.resolvedAt ? ` · resolved ${formatDate(inc.resolvedAt)}` : ''}
      </div>
      {inc.updates.map((u) => (
        <div key={u.id} style={{ marginTop: 8 }}>
          <span className="muted" style={{ fontSize: 12 }}>
            {formatDate(u.createdAt)} · {u.status}
          </span>
          <div style={{ whiteSpace: 'pre-wrap' }}>{u.body}</div>
        </div>
      ))}
    </div>
  );
}
